import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useEffect, useState } from "react";
import { CreateQnaReplyApiProps, ReplyEntry, UpdateQnaReplyApiProps } from "../../../../types/support/qna";
import { deleteQnaReplyApi, updateQnaReplyApi, createQnaReplyApi } from "../../../../api/support/qna";
import { useModal } from "../../../../hooks";
import useFileHandler from "../../../../hooks/useFileHandler";

export const DefaultInputData = {
    qnaId: '',
    qnaReplyId: '',
    userId: 0,
    userName: '',
    body: '',
    regAt: '',
    isMain: 'N',
    fileList: [],
    fileVOList: [],
    fileKeyList: []
};

interface EditQnaReplyProps {
    data?: ReplyEntry;
    onCanceled?: (key: string) => void;
    qnaId: string;
}

/**
 * QNA reply CRUD hook
 * @returns
 */
export const useEditQnaReply = ({ data, onCanceled, qnaId }: EditQnaReplyProps) => {
    const queryClient = useQueryClient();
    const { handleModal } = useModal();
    const [inputData, setInputData] = useState<ReplyEntry>(data ?? DefaultInputData);
    const { removeFile, handleButtonClick, UploadUI } = useFileHandler({ setter: setInputData });

    const deleteMutation = useMutation({
        mutationFn: (data: string) => deleteQnaReplyApi(data),
        onSuccess: (res) => {
            queryClient.invalidateQueries({
                queryKey: ["readQnaDetailApi", qnaId],
            });
            handleModal({
                isOpen: true,
                type: 'alert',
                contents: '삭제되었습니다.',
            });
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const updateMutation = useMutation({
        mutationFn: (data: UpdateQnaReplyApiProps) => updateQnaReplyApi(data),
        onSuccess: (res, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["readQnaDetailApi", qnaId],
            });
            onCanceled && onCanceled(variables.qnaReplyId);
            handleModal({
                isOpen: true,
                type: 'alert',
                contents: '수정되었습니다.',
            });
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const createMutation = useMutation({
        mutationFn: (data: CreateQnaReplyApiProps) => createQnaReplyApi(data),
        onSuccess: (res) => {
            queryClient.invalidateQueries({
                queryKey: ["readQnaDetailApi", qnaId],
            });
            onCanceled && onCanceled(res.result.qnaReplyId);
            handleModal({
                isOpen: true,
                type: 'alert',
                contents: '저장되었습니다.',
            });
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const deleteQnaReply = (data: string) => {
        handleModal({
            isOpen: true,
            type: 'confirm',
            contents: "선택한 답글을 삭제하시겠습니까?",
            title: "알림",
            callback: () => {
                deleteMutation.mutate(data);
            }
        });
    };

    const updateQnaReply = (data: UpdateQnaReplyApiProps) => {
        updateMutation.mutate(data);
    }

    const createQnaReply = (data: CreateQnaReplyApiProps) => {
        createMutation.mutate(data);
    }

    const onChangeInputData = (
        e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setInputData({ ...inputData, [name]: value });
    };

    useEffect(() => {
        setInputData(data ?? DefaultInputData);
    }, [data]);

    return {
        deleteQnaReply,
        updateQnaReply,
        createQnaReply,
        inputData,
        onChangeInputData,
        setInputData,
        UploadUI,
        handleButtonClick,
        removeFile
    };
};
